import { SingleDateField } from '../calendar';
import {
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  type SxProps,
  type Theme,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface DateTimeRangeProps {
  date?: string | null;
  disabled?: boolean;
  dateLabel?: string;
  errors?: {
    date?: boolean;
    startTime?: boolean;
    endTime?: boolean;
  };
  startTime?: Dayjs | string | null;
  showStartTime?: boolean;
  showEndTime?: boolean;
  endTime?: Dayjs | string | null;
  onChangeDate?: (_v: Dayjs | null) => void;
  onChangeTimeFrom?: (_v: Dayjs | null) => void;
  onChangeTimeTo?: (_v: Dayjs | null) => void;
  'data-testid'?: string;
}

/**
 * THE TIMES READ LIKE THE DATE BESIDE THEM: 14/500, the input value's type (Figma
 * 22543:49309 and the design system's time-picker field). The theme letters every Select
 * value 12/700, and this override sat at that rule's specificity, so stylesheet order
 * decided it: regular in the app, bold in Storybook. `&&` doubles this class and settles
 * it (#535).
 */
const TIME_SELECT_SX: SxProps<Theme> = {
  '&& .MuiSelect-select': {
    fontSize: '0.875rem',
    fontWeight: 500,
    paddingInline: '18px !important',
    textAlign: 'center',
  },
};

/** An unset time shows the faded placeholder the date field shows, not an italic label. */
const renderTime = (value: string) =>
  value || (
    <Box component='span' sx={{ color: 'text.secondary', opacity: 0.5 }}>
      Select time
    </Box>
  );

export interface DateTimeRangeData {
  date?: string | null;
  startTime?: Dayjs | string | null;
  endTime?: Dayjs | string | null;
}

const SelectedDateContext = React.createContext<{
  selected?: Date;
  setSelected?: React.Dispatch<React.SetStateAction<Date | undefined>>;
}>({});

const DateTimeRange: React.FC<DateTimeRangeProps> = ({
  date,
  disabled = false,
  dateLabel,
  errors = {},
  startTime,
  endTime,
  showStartTime = false,
  showEndTime = false,
  onChangeDate,
  onChangeTimeFrom,
  onChangeTimeTo,
  'data-testid': testId,
  ...rest
}) => {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });

  // Generate time options with 15-minute intervals
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push({
          value: timeString,
          label: timeString,
          dayjsValue: dayjs().hour(hour).minute(minute).second(0).millisecond(0),
        });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const handleDateChange = (v: Dayjs | null) => {
    onChangeDate?.(v);
  };

  const handleStartTimeSelectChange = (event: SelectChangeEvent<string>) => {
    const selectedTime = timeOptions.find((option) => option.value === event.target.value);
    if (selectedTime) {
      onChangeTimeFrom?.(selectedTime.dayjsValue);
    }
  };

  const handleEndTimeSelectChange = (event: SelectChangeEvent<string>) => {
    const selectedTime = timeOptions.find((option) => option.value === event.target.value);
    if (selectedTime) {
      onChangeTimeTo?.(selectedTime.dayjsValue);
    }
  };

  return (
    <SelectedDateContext.Provider
      value={{
        selected: date ? dayjs(date).toDate() : undefined,
        setSelected: (v) => {
          if (typeof v === 'function') return;
          onChangeDate?.(v ? dayjs(v) : null);
        },
      }}
    >
      <Stack direction='row' spacing={2} data-testid={testId} {...rest}>
        <Stack spacing={1}>
          {dateLabel && <FormLabel>{dateLabel}</FormLabel>}
          {/* The meeting's day as the calendar speaks it; the form still hears a Dayjs at
              local midnight, as the MUI picker handed it over. */}
          <SingleDateField
            disabled={disabled}
            error={errors.date}
            // A meeting always has a day: emptying the text puts it back, as before.
            clearable={false}
            value={date && dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : null}
            onChange={(day) => handleDateChange(day ? dayjs(day) : null)}
            label={dateLabel}
            data-testid={testId ? `${testId}-date-picker` : 'date-picker-button'}
          />
        </Stack>
        {(showStartTime || showEndTime) && (
          <Stack justifyContent='flex-start' spacing={1}>
            {showStartTime && <FormLabel>{t('time')}</FormLabel>}
            <Stack direction='row' alignItems='center' spacing={1}>
              {showStartTime && (
                <FormControl size='small'>
                  <Select
                    IconComponent={() => null}
                    disabled={disabled}
                    value={
                      startTime
                        ? typeof startTime === 'string'
                          ? dayjs(startTime).format('HH:mm')
                          : startTime.format('HH:mm')
                        : ''
                    }
                    onChange={handleStartTimeSelectChange}
                    displayEmpty
                    error={errors.startTime}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                          overflow: 'auto',
                        },
                      },
                    }}
                    sx={TIME_SELECT_SX}
                    renderValue={renderTime}
                    inputProps={{
                      'data-testid': testId ? `${testId}-start-time` : 'start-time-select',
                    }}
                  >
                    <MenuItem value=''>
                      <em>Select time</em>
                    </MenuItem>
                    {timeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {showEndTime && (
                <>
                  <FormLabel component='span'>{t('to')}</FormLabel>
                  <FormControl size='small'>
                    <Select
                      displayEmpty
                      IconComponent={() => null}
                      disabled={disabled}
                      value={
                        endTime
                          ? typeof endTime === 'string'
                            ? dayjs(endTime).format('HH:mm')
                            : endTime.format('HH:mm')
                          : ''
                      }
                      onChange={handleEndTimeSelectChange}
                      error={errors.endTime}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 200,
                            overflow: 'auto',
                          },
                        },
                      }}
                      sx={TIME_SELECT_SX}
                      renderValue={renderTime}
                      inputProps={{
                        'data-testid': testId ? `${testId}-end-time` : 'end-time-select',
                      }}
                    >
                      <MenuItem value=''>
                        <em>Select time</em>
                      </MenuItem>
                      {timeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </SelectedDateContext.Provider>
  );
};

export default DateTimeRange;
